<template>
  <b-form @submit.prevent="onSubmit">
    <b-row class="mb-3">
      <b-col cols="12" md="4" lg="2">
        <b-form-group>
          <template #label>
            Năm <span class="text-danger">*</span>
          </template>
          <date-picker
            ref="yearDatePicker"
            class="form-control form-control-sm declare-year-picker"
            :config="yearPickerConfig"
            v-model="yearModel"
            placeholder="Chọn năm"
            :disabled="isEdit"
            @input="onYearChange"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="8" lg="4">
        <b-form-group>
          <template>
            <label>Doanh nghiệp</label>
            <span class="text-danger">*</span>
          </template>
          <b-form-select
            size="sm"
            v-model="form.companyId"
            :options="listCompany"
            value-field="id"
            text-field="name"
            required
            :disabled="disabled"
          >
            <template v-slot:first>
              <b-form-select-option :value="null" disabled>
                -- Doanh nghiệp --
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6" lg="6">
        <b-form-group>
          <template #label>
            Phòng ban <span class="text-danger">*</span>
          </template>
          <Autosuggest
            :model="departmentModel"
            :suggestions="departmentSuggestions"
            :suggestionName="'name'"
            :placeholder="'phòng ban'"
            :limit="50"
            :disabled="disabled || isLoadingFilters"
            @select="onSelected($event, 'Department')"
            @change="onInputed($event, 'Department')"
          >
            <template #custom="{ suggestion }">
              {{ suggestion.item.name }}
            </template>
          </Autosuggest>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col cols="12" md="6" lg="6">
        <b-form-group>
          <template #label>
            Khoản mục phí <span class="text-danger">*</span>
          </template>
          <Autosuggest
            :model="costItemModel"
            :suggestions="costItemSuggestions"
            :suggestionName="'name'"
            :placeholder="'khoản mục phí'"
            :limit="50"
            :disabled="disabled || isLoadingFilters"
            @select="onSelected($event, 'CostItem')"
            @change="onInputed($event, 'CostItem')"
          >
            <template #custom="{ suggestion }">
              {{ suggestion.item.name }}
            </template>
          </Autosuggest>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6" lg="6">
        <b-form-group>
          <template #label>
            <span>Cửa hàng</span> <span class="text-danger">*</span>
          </template>
          <Autosuggest
            :model="storeModel"
            :suggestions="storeSuggestions"
            :suggestionName="'name'"
            :placeholder="'cửa hàng'"
            :limit="50"
            :disabled="disabled || isLoadingFilters"
            @select="onSelected($event, 'Store')"
            @change="onInputed($event, 'Store')"
          >
            <template #custom="{ suggestion }">
              {{ suggestion.item.name }}
            </template>
          </Autosuggest>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group v-if="isEdit" class="mb-3">
      <template #label>Diễn giải</template>
      <b-form-textarea
        v-model="editDescription"
        rows="2"
        placeholder="Nhập diễn giải (optional)"
        :disabled="disabled"
      />
    </b-form-group>

    <h5 class="mb-3">Phân bổ ngân sách theo tháng</h5>
    <b-row>
      <b-col cols="3" v-for="m in 12" :key="m" class="mb-3">
        <b-form-group :label="'Tháng ' + m">
          <BInputNumber
            v-model="monthAmounts[m - 1]"
            placeholder="0"
            size="sm"
            :min="0"
            :disabled="disabled"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col>
        <strong>Tổng: {{ convertPrice(totalAmount) }}</strong>
      </b-col>
    </b-row>

    <b-row class="mb-4" v-if="!isEdit">
      <b-col cols="12" lg="8">
        <ApprovalStepSearchSelector
          :steps="approvalSteps"
          :title="'Quy trình duyệt ngân sách'"
          :emptyText="'Chưa có cấu hình duyệt cho ngân sách.'"
          @change="onApprovalStepsChange"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-button
          v-if="showSubmit"
          type="submit"
          variant="primary"
          :disabled="disabled"
        >
          <i class="fas fa-save"></i>
          {{ disabled ? 'Đang lưu...' : submitText }}
        </b-button>
        <b-button
          v-if="showBack"
          variant="secondary"
          class="ml-2"
          @click="$emit('back')"
          :disabled="disabled"
        >
          <i class="fas fa-arrow-left"></i> Quay lại
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import ApiService from '@/core/services/api.service';
import apiCommonMixin from '@/view/mixins/api-common.mixins.js';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ApprovalStepSearchSelector from '@/view/components/approval/ApprovalStepSearchSelector.vue';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  xoa_dau,
} from '@/utils/common';
import { mergeApprovalConfigDetailsByStep } from '@/utils/approval-config';
import timeUtils from '@/utils/date';
import moment from 'moment';

export default {
  name: 'BudgetLineDeclareForm',
  components: { Autosuggest, ApprovalStepSearchSelector, BInputNumber },
  mixins: [apiCommonMixin],
  props: {
    mode: { type: String, default: 'create' }, // 'create' | 'edit'
    disabled: { type: Boolean, default: false },
    showBack: { type: Boolean, default: true },
    showSubmit: { type: Boolean, default: true },
    submitText: { type: String, default: 'Khai báo' },
    initialValue: { type: Object, default: () => ({}) }, // { year, companyId, departmentId, costItemId, storeId, monthAmounts:[], ... }
  },
  data() {
    return {
      isLoadingFilters: false,
      approvalType: 8,
      yearModel: moment(String(new Date().getFullYear()), 'YYYY'),
      yearPickerConfig: {
        ...timeUtils.DP_CONFIG.date,
        format: 'YYYY',
        viewMode: 'years',
        ignoreReadonly: true,
        allowInputToggle: true,
        useCurrent: false,
        showClear: true,
        showClose: true,
      },
      listCompany: [],
      approvalSteps: [],
      form: {
        year: new Date().getFullYear(),
        companyId: null,
        departmentId: null,
        costItemId: null,
        storeId: null,
      },
      editDescription: '',
      departmentModel: '',
      costItemModel: '',
      storeModel: '',
      monthAmounts: new Array(12).fill(0),
      suggestLimit: 50,
      departmentSource: [],
      costItemSource: [],
      storeSource: [],
      departmentSuggestions: [],
      costItemSuggestions: [],
      storeSuggestions: [],
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    totalAmount() {
      return this.monthAmounts.reduce((sum, v) => sum + (Number(v) || 0), 0);
    },
  },
  created() {
    this.loadCompanies();
    this.loadDepartments();
    this.loadStores();
    this.fetchCostItems();
    if (!this.isEdit) this.fetchApprovalConfig();
    this.applyInitialValue();
  },
  mounted() {
    // bootstrap-datetimepicker render input text -> make readonly to prevent typing
    this.$nextTick(() => {
      const root = this.$refs.yearDatePicker && this.$refs.yearDatePicker.$el;
      const input =
        root && root.tagName === 'INPUT'
          ? root
          : root && root.querySelector && root.querySelector('input');
      if (input) {
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('autocomplete', 'off');
      }
    });
  },
  watch: {
    initialValue: {
      deep: true,
      handler() {
        this.applyInitialValue();
      },
    },
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    /** Giống EmployeeInfo: filter theo @change + xoa_dau */
    filterSuggestionsByName(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) {
        return list.slice(0, limit);
      }
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          const nameNoneSign = xoa_dau(name);
          return (
            nameNoneSign.toLowerCase().indexOf(tLower) > -1 ||
            name.toLowerCase().indexOf(tLower) > -1
          );
        })
        .slice(0, limit);
    },
    filterStoreSuggestions(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) {
        return list.slice(0, limit);
      }
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          const shortName =
            item && item.shortName != null ? String(item.shortName) : '';
          const fields = [name, shortName].filter(Boolean);
          return fields.some((field) => {
            const none = xoa_dau(field);
            return (
              none.toLowerCase().indexOf(tLower) > -1 ||
              field.toLowerCase().indexOf(tLower) > -1
            );
          });
        })
        .slice(0, limit);
    },
    syncDepartmentSuggestionsFromModel() {
      this.departmentSuggestions = this.filterSuggestionsByName(
        this.departmentSource,
        this.departmentModel,
        this.suggestLimit,
      );
    },
    syncCostItemSuggestionsFromModel() {
      this.costItemSuggestions = this.filterSuggestionsByName(
        this.costItemSource,
        this.costItemModel,
        this.suggestLimit,
      );
    },
    syncStoreSuggestionsFromModel() {
      this.storeSuggestions = this.filterStoreSuggestions(
        this.storeSource,
        this.storeModel,
        this.suggestLimit,
      );
    },
    applyInitialValue() {
      const v = this.initialValue || {};
      console.log('[BudgetLineDeclareForm] applyInitialValue.initialValue', v);
      if (v.year) {
        this.form.year = Number(v.year);
        this.yearModel = moment(String(this.form.year), 'YYYY');
      }
      if (v.companyId !== undefined) this.form.companyId = v.companyId;
      if (v.departmentId !== undefined) this.form.departmentId = v.departmentId;
      if (v.costItemId !== undefined) this.form.costItemId = v.costItemId;
      if (v.storeId !== undefined) this.form.storeId = v.storeId;

      this.departmentModel = v.departmentName || this.departmentModel || '';
      this.costItemModel = v.costItemName || this.costItemModel || '';
      this.storeModel = v.storeName || this.storeModel || '';
      console.log('[BudgetLineDeclareForm] applyInitialValue.models', {
        departmentId: this.form.departmentId,
        departmentModel: this.departmentModel,
        storeId: this.form.storeId,
        storeModel: this.storeModel,
        costItemId: this.form.costItemId,
        costItemModel: this.costItemModel,
      });

      if (Array.isArray(v.monthAmounts) && v.monthAmounts.length === 12) {
        this.monthAmounts = [...v.monthAmounts];
      }
      if (v.description !== undefined) this.editDescription = v.description || '';
      this.$nextTick(() => {
        this.syncDepartmentSuggestionsFromModel();
        this.syncCostItemSuggestionsFromModel();
        this.syncStoreSuggestionsFromModel();
      });
    },
    loadCompanies() {
      this.isLoadingFilters = true;
      return this.fetchCompany()
        .then((res) => {
          this.listCompany = res.data || [];
        })
        .catch(() => {
          this.listCompany = [];
        })
        .finally(() => {
          this.isLoadingFilters = false;
        });
    },
    loadDepartments() {
      this.isLoadingFilters = true;
      return this.fetchDepartments()
        .then((res) => {
          const rows = res.data || [];
          this.departmentSource = rows.map((d) => ({
            id: Number(d.id),
            name: d.name,
          }));
          this.syncDepartmentSuggestionsFromModel();
        })
        .catch(() => {
          this.departmentSource = [];
          this.departmentSuggestions = [];
        })
        .finally(() => {
          this.isLoadingFilters = false;
        });
    },
    loadStores() {
      this.isLoadingFilters = true;
      return this.fetchStores()
        .then((res) => {
          const rows = res.data || [];
          this.storeSource = rows.map((s) => ({
            id: Number(s.id),
            name: s.name,
            shortName: s.shortName,
          }));
          this.syncStoreSuggestionsFromModel();
        })
        .catch(() => {
          this.storeSource = [];
          this.storeSuggestions = [];
        })
        .finally(() => {
          this.isLoadingFilters = false;
        });
    },
    onSelected(option, type) {
      const item = option && option.item ? option.item : null;
      if (type === 'Department') {
        this.form.departmentId = item ? Number(item.id) : null;
        this.departmentModel = item ? item.name : '';
        this.departmentSuggestions = this.departmentSource.slice(
          0,
          this.suggestLimit,
        );
        return;
      }
      if (type === 'Store') {
        this.form.storeId = item ? Number(item.id) : null;
        this.storeModel = item
          ? item.shortName
            ? `${item.name} (${item.shortName})`
            : item.name
          : '';
        this.storeSuggestions = this.storeSource.slice(0, this.suggestLimit);
        return;
      }
      if (type === 'CostItem') {
        this.form.costItemId = item ? Number(item.id) : null;
        this.costItemModel = item ? item.name : '';
        this.costItemSuggestions = this.costItemSource.slice(
          0,
          this.suggestLimit,
        );
      }
    },
    onInputed(text, type) {
      const value = (text || '').toString();
      if (type === 'Department') {
        this.departmentModel = value;
        if (!value.trim()) this.form.departmentId = null;
        this.syncDepartmentSuggestionsFromModel();
        return;
      }
      if (type === 'Store') {
        this.storeModel = value;
        if (!value.trim()) this.form.storeId = null;
        this.syncStoreSuggestionsFromModel();
        return;
      }
      if (type === 'CostItem') {
        this.costItemModel = value;
        if (!value.trim()) this.form.costItemId = null;
        this.syncCostItemSuggestionsFromModel();
      }
    },
    onYearChange(value) {
      if (this.isEdit) return;
      let year = null;
      if (value == null) return;
      if (moment.isMoment(value)) year = value.year();
      else if (value instanceof Date) year = value.getFullYear();
      else if (typeof value === 'number') year = value;
      else if (typeof value === 'string') {
        const match = value.match(/\d{4}/);
        year = match ? Number(match[0]) : null;
      }
      if (year && year >= 2020 && year <= 2100) {
        this.form.year = year;
        this.yearModel = moment(String(year), 'YYYY');
      }
    },
    fetchCostItems() {
      ApiService.get('v2/cost-items/all').then(({ data }) => {
        const items = data.data || [];
        this.costItemSource = items.map((i) => ({
          id: Number(i.id),
          name: i.name,
        }));
        this.syncCostItemSuggestionsFromModel();
      });
    },
    mapApprovalSteps(details) {
      const merged = mergeApprovalConfigDetailsByStep(details || []);
      this.approvalSteps = merged.map((detail) => ({
        step: detail.step,
        approverBy: null,
        owners: (detail.owners || [])
          .filter((owner) => owner && owner.id)
          .filter(
            (owner, idx, arr) =>
              arr.findIndex((x) => Number(x.id) === Number(owner.id)) === idx,
          )
          .map((owner) => ({
            id: Number(owner.id),
            name: owner.name ? owner.name : `${owner.id}`,
          })),
      }));
    },
    onApprovalStepsChange(steps) {
      this.approvalSteps = steps || [];
    },
    fetchApprovalConfig() {
      ApiService.get(`v2/approval-configs/by-type/${this.approvalType}`)
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.mapApprovalSteps(data.data.details || []);
          } else {
            this.approvalSteps = [];
          }
        })
        .catch(() => {
          this.approvalSteps = [];
        });
    },
    buildMonthsPayload() {
      return this.monthAmounts
        .map((amount, idx) => ({
          month: idx + 1,
          allocatedAmount: Number(amount) || 0,
        }))
        .filter((m) => m.allocatedAmount >= 0);
    },
    validateCommon() {
      if (!this.form.companyId) {
        this.makeToastFaile('Vui lòng chọn doanh nghiệp!');
        return false;
      }
      if (!this.form.departmentId || !this.form.storeId || !this.form.costItemId) {
        this.makeToastFaile('Vui lòng chọn phòng ban, cửa hàng và khoản mục phí!');
        return false;
      }
      const months = this.buildMonthsPayload().filter((m) => m.allocatedAmount > 0);
      if (!months.length) {
        this.makeToastFaile('Vui lòng nhập ngân sách ít nhất 1 tháng!');
        return false;
      }
      return true;
    },
    onSubmit() {
      if (!this.validateCommon()) return;

      const payload = {
        ...this.form,
        months: this.buildMonthsPayload(),
      };

      if (this.isEdit) {
        const description =
          this.editDescription != null ? String(this.editDescription).trim() : '';
        this.$emit('submit', { ...payload, description });
        return;
      }

      if (!this.approvalSteps.length) {
        this.makeToastFaile('Chưa có cấu hình quy trình duyệt ngân sách!');
        return;
      }
      const invalidStep = this.approvalSteps.find((step) => !step.approverBy);
      if (invalidStep) {
        this.makeToastFaile(`Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`);
        return;
      }

      this.$emit('submit', {
        ...payload,
        approvalType: this.approvalType,
        approvers: this.approvalSteps
          .map((step) => ({ step: step.step, approverBy: step.approverBy }))
          .sort((a, b) => a.step - b.step),
      });
    },
  },
};
</script>

